import { z } from 'zod';
/**
 * CAN telemetry ingestion. Design: docs/architecture/can-telemetry-design.md §5.
 *
 * The parameter catalogue is the single source of truth shared by validation,
 * the known/unknown split, and the storage columns — the names here are the
 * column names, taken verbatim from Appendix A.
 *
 * `integer` exists because two parameters are `smallint` in storage
 * (`gearPosition`, `activeDtcCount`): a gear of 6.5 is not a rounding question,
 * it is a provider bug worth surfacing.
 */
export const TELEMETRY_PARAMETERS = {
    vehicleSpeedKph: 'number',
    odometerKm: 'number',
    engineRunHours: 'number',
    ignitionOn: 'boolean',
    latitude: 'number',
    longitude: 'number',
    engineRpm: 'number',
    engineLoadPct: 'number',
    engineCoolantTempC: 'number',
    engineOilPressureKpa: 'number',
    engineOilTempC: 'number',
    intakeManifoldTempC: 'number',
    boostPressureKpa: 'number',
    exhaustGasTempC: 'number',
    fuelLevelPct: 'number',
    fuelRateLph: 'number',
    totalFuelUsedL: 'number',
    batteryVoltageV: 'number',
    alternatorVoltageV: 'number',
    dpfSootLoadPct: 'number',
    dpfDiffPressureKpa: 'number',
    defLevelPct: 'number',
    scrInletTempC: 'number',
    transmissionOilTempC: 'number',
    gearPosition: 'integer',
    airPressurePrimaryKpa: 'number',
    airPressureSecondaryKpa: 'number',
    ambientTempC: 'number',
    malfunctionLamp: 'boolean',
    activeDtcCount: 'integer',
    engineTorqueActualPct: 'number',
    engineTorqueDemandPct: 'number',
    acceleratorPedalPct: 'number',
    serviceBrakeActive: 'boolean',
    retarderTorquePct: 'number',
    grossCombinationWeightKg: 'number',
    defLevelLitres: 'number',
    driverSeatbeltFastened: 'boolean',
    passengerSeatbeltFastened: 'boolean',
    alarmTriggered: 'boolean',
    engineTempC: 'number',
    // Added 28 July 2026, at AjnaView's confirmation that both are transmitted.
    // Brake pedal POSITION, distinct from `serviceBrakeActive` — the flag says the
    // brake is applied, this says how hard, and a rule about harsh braking needs
    // the second.
    brakePedalPct: 'number',
    cruiseControlActive: 'boolean',
    /*
     * Electric drivetrain, added 5 September 2026.
     *
     * The 43 names above are a DIESEL vocabulary — soot load, AdBlue, oil
     * pressure, fuel rate — and an EV reports none of them because it has none of
     * that hardware. Without these it can only report the basic tier, so a fleet
     * electrifying loses engine visibility the moment it replaces a lorry, and the
     * platform has nothing to say about the single most operationally important
     * number an EV has: how much charge is left.
     *
     * `chargingActive` is a BOOLEAN rather than a state string, because the kind
     * system here is number/integer/boolean and a fourth kind is a change to
     * validation, storage and the picker — for a value that is only ever asked as
     * "is it on charge right now?". "Charging" versus "In use" is derived from it
     * beside `vehicleSpeedKph`, which is where that distinction actually lives.
     *
     * `packCurrentA` is SIGNED on purpose: negative is current flowing back in.
     * Storing the magnitude would make charging and discharging indistinguishable
     * — the one comparison the field exists for.
     */
    stateOfChargePct: 'number',
    packVoltageV: 'number',
    packCurrentA: 'number',
    motorTempC: 'number',
    batteryTempC: 'number',
    regenBrakingKw: 'number',
    estimatedRangeKm: 'number',
    chargingActive: 'boolean',
    /*
     * Power take-off and AdBlue warning state, added 10 September 2026 after
     * reconciling AjnaView's real CAN payload. Both arrive from the provider as
     * ENUMS, and the kind system here is number/integer/boolean — so neither is
     * stored in the shape it was sent, and the translation is the interesting
     * part rather than an implementation detail.
     *
     * `ptoEngaged` is a BOOLEAN, not the provider's three-valued state. AjnaView
     * sends 0 off, 1 set, 2 NOT AVAILABLE — and "not available" is an absence
     * wearing a number. Storing 2 would make "we cannot tell" look like a
     * measurement, so the adapter omits the parameter entirely for that case and
     * the column stays NULL. Absent is not zero, at every layer.
     *
     * As a boolean it also joins the state timeline for free, which is where a
     * PTO belongs: an operator wants the engagement PERIODS on a tipper or a
     * mixer, not a scatter of instants.
     *
     * `defWarningLevel` is an ORDERED severity, and it is deliberately NOT the
     * provider's encoding. AjnaView sends 0 off, 1 red, 2 yellow, 3 info — which
     * is not monotonic: 1 is the most severe and 3 the least. Stored raw, a
     * perfectly reasonable policy condition `defWarningLevel >= 2` would mean
     * "yellow or info, but NOT red" — the exact opposite of what its author
     * intended, silently. The adapter reverses it so higher always means worse,
     * because a value that cannot be compared is worse than no value at all.
     */
    ptoEngaged: 'boolean',
    defWarningLevel: 'integer',
    /*
     * ── Tachograph ──────────────────────────────────────────────────────────
     *
     * `tachograph-parameters.md`. Twenty-one names: nine per DRIVER SLOT and
     * three at vehicle level. Every one is tier `TACHO`, because none can be read
     * without a tachograph fitted — the same hardware test that separates `BASIC`
     * from `CAN`.
     *
     * **The DEVICE computes these.** OIP stores, shows and (later) thresholds
     * them; it does not implement drivers'-hours regulations. That is what makes
     * the catalogue safe to add before Q5 is answered — a stored counter is the
     * legally-approved unit's own number whichever ruleset applies to the
     * vehicle, whereas a "time remaining" OIP calculated would be right for one
     * ruleset and wrong for the other.
     *
     * ⚠️ **Every counter is MINUTES**, confirmed against the FMC650 table rather
     * than inferred — though the sample is self-consistent with it: continuous
     * drive 175 + time to break 95 = 270, which is the 4h30 limit exactly. The
     * names carry the unit for the reason `defLevelPct` does.
     *
     * ⚠️ **Two ENUMS here have sentinels that are ABSENCES, not states.** The
     * work state is 0–7 with `6 Error` and `7 Not available`; card presence is
     * 0–3 with `2 Error` and `3 Not available (privacy mode)`. Stored as values
     * they would appear on a timeline as though the driver were doing something,
     * and a rule reading "anything not Drive is rest" would turn a sensor fault
     * into a rest period. The adapter maps both to absence — the column stays
     * NULL — exactly as `ptoState: 2` already does.
     *
     * ⚠️ **`tachoTimeStateCode` is a CATEGORY, not a scale.** `2` is *4.5h
     * reached* and `11` is *card expiry warning* — unrelated facts. Unlike
     * `defWarningLevel`, there is no ordering to recover, so DO NOT invent one:
     * a condition `>= 2` would match both. Rules must use equality, or a derived
     * boolean per case decided when the rule is written. It is catalogued with no
     * alias because AVL 189/190 is absent from AjnaView's payload today (§8 Q1) —
     * the name exists so the column is ready, and nothing writes it yet.
     */
    tachoTimeToBreakMin1: 'number',
    tachoTimeToBreakMin2: 'number',
    tachoTimeStateCode1: 'integer',
    tachoTimeStateCode2: 'integer',
    tachoWorkState1: 'integer',
    tachoWorkState2: 'integer',
    tachoCardPresent1: 'boolean',
    tachoCardPresent2: 'boolean',
    tachoCardState1: 'integer',
    tachoCardState2: 'integer',
    tachoContinuousDriveMin1: 'number',
    tachoContinuousDriveMin2: 'number',
    tachoBreakMin1: 'number',
    tachoBreakMin2: 'number',
    tachoDailyDriveMin1: 'number',
    tachoDailyDriveMin2: 'number',
    tachoWeeklyDriveMin1: 'number',
    tachoWeeklyDriveMin2: 'number',
    tachoTimeToDailyRestMin1: 'number',
    tachoTimeToDailyRestMin2: 'number',
    tachoSpeedKph: 'number',
    tachoOverSpeed: 'boolean',
    tachoDriverRecognised: 'boolean',
};
export const TELEMETRY_PARAMETER_NAMES = Object.keys(TELEMETRY_PARAMETERS);
/**
 * Which of the two telemetry tiers a parameter belongs to
 * (`telemetry-tiers-design.md` D2).
 *
 * **The boundary is the HARDWARE, not the price list.** These seven come off any
 * tracker with a GPS module and a power feed; everything else needs a device
 * wired to the vehicle bus. That line is physical, which is what makes it a good
 * tier boundary — nobody has to argue about which side a parameter falls on, and
 * a customer with a plain tracker cannot produce the other 36 whatever they pay.
 *
 * Declared HERE, beside the catalogue, and never as a second list. A parameter
 * added to `TELEMETRY_PARAMETERS` without a tier would be in the catalogue and
 * in no tier — silently unstorable for every tenant — which is the shape of
 * defect `POLICY_FIELDS` and `PROMPT_VARIABLES` both exist to prevent. The
 * `satisfies` clause makes that a compile error rather than an oversight, and
 * `telemetry-tiers.test.ts` asserts the two lists agree exactly.
 *
 * `odometerKm`, `engineRunHours` and `batteryVoltageV` are on the basic side
 * deliberately: every consumer tracker reports odometer and supply voltage, and
 * an hour meter is commonly a separate wire rather than a bus reading. They are
 * also the three most likely to be argued about, which is why they are named.
 */
export const TELEMETRY_PARAMETER_TIERS = {
    latitude: 'BASIC',
    longitude: 'BASIC',
    vehicleSpeedKph: 'BASIC',
    ignitionOn: 'BASIC',
    odometerKm: 'BASIC',
    batteryVoltageV: 'BASIC',
    engineRunHours: 'BASIC',
    engineRpm: 'CAN',
    engineLoadPct: 'CAN',
    engineCoolantTempC: 'CAN',
    engineOilPressureKpa: 'CAN',
    engineOilTempC: 'CAN',
    intakeManifoldTempC: 'CAN',
    boostPressureKpa: 'CAN',
    exhaustGasTempC: 'CAN',
    fuelLevelPct: 'CAN',
    fuelRateLph: 'CAN',
    totalFuelUsedL: 'CAN',
    alternatorVoltageV: 'CAN',
    dpfSootLoadPct: 'CAN',
    dpfDiffPressureKpa: 'CAN',
    defLevelPct: 'CAN',
    scrInletTempC: 'CAN',
    transmissionOilTempC: 'CAN',
    gearPosition: 'CAN',
    airPressurePrimaryKpa: 'CAN',
    airPressureSecondaryKpa: 'CAN',
    ambientTempC: 'CAN',
    malfunctionLamp: 'CAN',
    activeDtcCount: 'CAN',
    engineTorqueActualPct: 'CAN',
    engineTorqueDemandPct: 'CAN',
    acceleratorPedalPct: 'CAN',
    serviceBrakeActive: 'CAN',
    retarderTorquePct: 'CAN',
    grossCombinationWeightKg: 'CAN',
    defLevelLitres: 'CAN',
    driverSeatbeltFastened: 'CAN',
    passengerSeatbeltFastened: 'CAN',
    alarmTriggered: 'CAN',
    engineTempC: 'CAN',
    brakePedalPct: 'CAN',
    cruiseControlActive: 'CAN',
    /*
     * CAN, all of them, and the hardware test settles it exactly as it does for
     * the diesel parameters: none of these can be read without a device wired to
     * the vehicle bus. A plain tracker on an EV reports position, speed and its
     * own supply voltage, and knows nothing about the traction pack.
     *
     * `batteryVoltageV` stays BASIC and is a DIFFERENT quantity from
     * `packVoltageV` — the 12V auxiliary supply every tracker measures, not the
     * traction pack. Two voltages that must never be read as one.
     */
    stateOfChargePct: 'CAN',
    packVoltageV: 'CAN',
    packCurrentA: 'CAN',
    motorTempC: 'CAN',
    batteryTempC: 'CAN',
    regenBrakingKw: 'CAN',
    estimatedRangeKm: 'CAN',
    chargingActive: 'CAN',
    // Both need a bus. A PTO state comes off the body-builder network and the
    // AdBlue warning off the engine ECU; neither can be read from a GPS tracker.
    ptoEngaged: 'CAN',
    defWarningLevel: 'CAN',
    /*
     * ⚠️ `TACHO` does NOT imply `TELEMETRY`, and that asymmetry is deliberate —
     * see `FEATURES.TACHOGRAPH`. Engine data IS telemetry, so CAN implies the
     * lower tier; a tachograph is independent hardware, and a false implication
     * would silently grant `TELEMETRY` to every tacho customer and bill them for
     * it. Very hard to walk back once invoiced.
     */
    tachoTimeToBreakMin1: 'TACHO',
    tachoTimeToBreakMin2: 'TACHO',
    tachoTimeStateCode1: 'TACHO',
    tachoTimeStateCode2: 'TACHO',
    tachoWorkState1: 'TACHO',
    tachoWorkState2: 'TACHO',
    tachoCardPresent1: 'TACHO',
    tachoCardPresent2: 'TACHO',
    tachoCardState1: 'TACHO',
    tachoCardState2: 'TACHO',
    tachoContinuousDriveMin1: 'TACHO',
    tachoContinuousDriveMin2: 'TACHO',
    tachoBreakMin1: 'TACHO',
    tachoBreakMin2: 'TACHO',
    tachoDailyDriveMin1: 'TACHO',
    tachoDailyDriveMin2: 'TACHO',
    tachoWeeklyDriveMin1: 'TACHO',
    tachoWeeklyDriveMin2: 'TACHO',
    tachoTimeToDailyRestMin1: 'TACHO',
    tachoTimeToDailyRestMin2: 'TACHO',
    tachoSpeedKph: 'TACHO',
    tachoOverSpeed: 'TACHO',
    tachoDriverRecognised: 'TACHO',
};
/**
 * Which entitlement each tier requires.
 *
 * ⚠️ **The ONE place a tier is joined to a feature.** The ingestion filter used
 * to ask one question — `isEnabled(CAN_TELEMETRY)` — and branch on the answer,
 * which worked precisely because there were two tiers and one of them was
 * always held. A third tier cannot be expressed that way at all: a tenant
 * entitled to it but not to CAN would have had its parameters dropped by the
 * `else` branch, silently, because everything outside BASIC fell through the
 * same arm.
 *
 * `satisfies` makes a tier added without a feature a COMPILE error, exactly as
 * `TELEMETRY_PARAMETER_TIERS` makes a parameter added without a tier one. Adding
 * a tier is then a data change here plus its parameters, and no logic moves.
 */
export const TELEMETRY_TIER_FEATURE = {
    BASIC: 'TELEMETRY',
    CAN: 'CAN_TELEMETRY',
    TACHO: 'TACHOGRAPH',
};
/**
 * May a tenant holding `tiers` store this parameter?
 *
 * Pure, and exported so the decision exists ONCE. The ingestion filter is its
 * caller today; anything else that needs to know what a tenant may hold must
 * ask here rather than re-deriving it, because a second copy of an entitlement
 * rule is how `TenantSmtpConfig` went unread for months.
 *
 * ⚠️ An UNRECOGNISED name is kept. It is not a canonical parameter at all, so
 * it belongs to `extra` rather than to any tier — dropping it here would
 * discard a provider's new field as though it were an entitlement question, and
 * the whole point of `extra` is that adding a field can never be an outage.
 */
export function isParameterEntitled(name, tiers) {
    const tier = TELEMETRY_PARAMETER_TIERS[name];
    return tier === undefined || tiers.has(tier);
}
/** Parameters a tenant holding only `TELEMETRY` may store. */
export const BASIC_TELEMETRY_PARAMETERS = new Set(TELEMETRY_PARAMETER_NAMES.filter((n) => TELEMETRY_PARAMETER_TIERS[n] === 'BASIC'));
/**
 * The boolean parameters, DERIVED from the catalogue rather than listed.
 *
 * A hand-written list is what drifts, and this one already had: the web app's
 * picker documented "the six booleans" and named six, while the catalogue has
 * SEVEN — `cruiseControlActive` was added on 28 July 2026 and no list caught up.
 * Nothing was broken by that, which is why it survived; the comment was simply
 * wrong and would have been trusted.
 */
export const BOOLEAN_TELEMETRY_PARAMETERS = TELEMETRY_PARAMETER_NAMES.filter((n) => TELEMETRY_PARAMETERS[n] === 'boolean');
/**
 * The batch ENVELOPE only.
 *
 * Parameter names are NOT validated here, deliberately. Mapping a provider's
 * vocabulary to OIP's canonical names is the adapter's job
 * (`mapAjnaViewTelemetry`), and that seam is what keeps a provider rename from
 * becoming a schema migration. Validating canonical names at the HTTP edge
 * would put provider knowledge in two places and quietly defeat the adapter.
 *
 * Values are constrained, because a scalar is a scalar in any vocabulary; kinds
 * (which parameter must be an integer, which a boolean) are checked after
 * mapping, where the canonical catalogue is known.
 *
 * `null` is accepted and means what absence means — providers commonly emit an
 * explicit null for a sensor that did not answer. Neither is zero.
 */
export const telemetrySampleSchema = z
    .object({
    /**
     * The vehicle's registration, as the provider knows it.
     *
     * ⚠️ **OPTIONAL since 17 September 2026, and `deviceId` is the other half.** It was
     * required, which made this path accept a REGISTRATION and nothing else while the event
     * path had resolved `providerVehicleId → registration → name` — device id FIRST — since
     * the fuel-card work. The argument written there is equally true of a telemetry frame:
     * *a registration can be re-issued to another vehicle and a plate can be mistyped; a
     * device id is the provider's own key for the box that sent the data.*
     *
     * A provider keying live telemetry by device had every sample refused
     * `UNRESOLVED_VEHICLE` — visibly and counted, so diagnosable in minutes, but a feed that
     * does not start.
     */
    regNo: z.string().trim().min(1).max(60).optional(),
    /**
     * The provider's own identifier for the device — an IMEI, a unit id, whatever token
     * they key the box by. Matched against `Vehicle.providerDeviceId`.
     *
     * ⚠️ 120 characters rather than the registration's 60: an IMEI is 15 digits but a
     * provider's composite unit key is not bounded by anything we control, and a cap that
     * refuses a real identifier is a feed that cannot start for a reason nobody can act on.
     */
    deviceId: z.string().trim().min(1).max(120).optional(),
    occurredAt: z.coerce.date(),
    seq: z.number().int().nonnegative().optional(),
    /**
     * Who was driving, as the DEVICE identifies them — a tachograph card
     * number, or whatever opaque token the provider assembles for a driver.
     *
     * ⚠️ A FIRST-CLASS FIELD, not a parameter, and that is forced rather than
     * chosen: `TELEMETRY_PARAMETERS` is `number | integer | boolean`, and this
     * is a string. It is also better as a field — the identity is stored once
     * as a reference on the row rather than repeated as text on every sample,
     * and the NAME lives on `Driver` instead of being duplicated a thousand
     * times a day.
     *
     * ⚠️ Resolved, never CREATED. An unrecognised token leaves `driverId` null,
     * exactly as an unrecognised registration refuses rather than inventing a
     * vehicle (D12): a mistyped card would otherwise become a phantom driver
     * that incidents and scores attach to. The sample is kept either way —
     * losing a reading because a card is unknown would be the worse failure.
     *
     * Until this existed, `VehicleTelemetry.driverId` was populated only on
     * DERIVED events, from their trigger, so per-driver fuel economy and
     * `scoreDriverSafety` were permanently empty on any fleet that had not been
     * back-filled. This is the fix the trip and safety work both deferred.
     */
    driverRef: z.string().trim().min(1).max(64).optional(),
    /**
     * Slot 2's driver on a crewed vehicle, in the same form as `driverRef`.
     *
     * ⚠️ **Both or neither, never one field reused.** A single driver field
     * would let a crewed journey be recorded as one person's, and on a
     * tachograph that is not a rounding error — it is somebody else's hours.
     * Asked of the provider as a pair for exactly that reason.
     *
     * ⚠️ **Slot 2 is usually RESTING.** Nothing that measures DRIVING reads it:
     * distance, fuel economy and the safety score stay with slot 1, who drove.
     * What slot 2 owns is its own hours.
     */
    secondDriverRef: z.string().trim().min(1).max(64).optional(),
    /**
     * Every parameter is optional and a record need not carry all of them:
     * a vehicle may not report DPF load, a light van has no air pressure. A
     * sample with none at all is still a heartbeat.
     */
    parameters: z.record(z.union([z.number(), z.boolean(), z.null()])).default({}),
    /**
     * Full provider position snapshot (any JSON object). Stored in
     * VehicleTelemetry.extra — this is how a GPS/telematics platform can
     * forward the complete position without forcing every field through the
     * CAN parameter catalogue.
     */
    raw: z.record(z.string(), z.any()).optional(),
    /**
     * Active diagnostic trouble codes, where the provider sends them.
     *
     * A SIBLING of `parameters` rather than a member: every canonical parameter
     * is a scalar with a declared kind, and that is what lets the adapter reject
     * a gear of 6.5. A list of codes has no kind in that sense, so folding it in
     * would mean widening the value union and losing the check for all 43.
     *
     * The shape is validated here but the CODES are not interpreted — the edge
     * stays provider-agnostic, exactly as it does for parameter names.
     */
    dtcCodes: z
        .array(z
        .object({
        code: z.string().trim().min(1).max(32),
        description: z.string().trim().max(300).optional(),
        severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).optional(),
    })
        .strict())
        .max(64)
        .optional(),
})
    .strict()
    /*
     * ⚠️ **AT LEAST ONE identifier, and BOTH is allowed on purpose.** A provider that knows
     * both should send both: the resolver prefers the device and falls back to the plate, so a
     * frame carrying both survives a registration being corrected in our records while the box
     * keeps reporting. Requiring EXACTLY one would refuse the most informative payload
     * available, which is the opposite of what this field was added for.
     *
     * Refused HERE rather than at resolution, because a sample naming no vehicle at all cannot
     * be attributed to anything and would otherwise be counted as an unresolved REFERENCE —
     * putting a blank row on the screen a customer reads to find plates they need to create.
     */
    .refine((s) => s.regNo !== undefined || s.deviceId !== undefined, {
    message: 'Each sample must carry regNo, deviceId, or both',
    path: ['regNo'],
});
export const TELEMETRY_BATCH_MAX = 1_000;
export const telemetryBatchRequestSchema = z
    .object({
    samples: z.array(telemetrySampleSchema).min(1).max(TELEMETRY_BATCH_MAX),
})
    .strict();
/**
 * Why a single sample was refused. A closed vocabulary so the provider can
 * branch on it rather than parsing prose.
 */
export const telemetryRejectionReasonSchema = z.enum([
    'UNRESOLVED_VEHICLE',
    'INVALID_PARAMETERS',
    'OUT_OF_RETENTION',
    'TOO_FAR_FUTURE',
]);
export const telemetryRejectionSchema = z.object({
    /** Index within the submitted `samples` array, so the caller can locate it. */
    index: z.number().int().nonnegative(),
    regNo: z.string().nullable(),
    /**
     * The device the refused sample named, or null.
     *
     * ⚠️ Added with `deviceId` on the sample. Without it a DEVICE-ONLY sender reads
     * `regNo: null` and the only identifier left is the array index — fine for a machine,
     * useless in a support conversation about which box is being refused.
     */
    deviceId: z.string().nullable(),
    reason: telemetryRejectionReasonSchema,
    detail: z.string().optional(),
});
/**
 * A batch is PARTIALLY acceptable, by design.
 *
 * Rejecting a whole batch because part of it is invalid is the trap: the
 * provider retries, the same samples fail, and good data never lands — a poison
 * sample blocks the stream indefinitely and silently.
 */
export const telemetryBatchResponseSchema = z.object({
    accepted: z.number().int(),
    duplicates: z.number().int(),
    rejected: z.number().int(),
    rejections: z.array(telemetryRejectionSchema),
    /**
     * Parameter readings discarded because the tenant holds `TELEMETRY` and not
     * `CAN_TELEMETRY` (`telemetry-tiers-design.md` D3).
     *
     * NOT a rejection: the sample was kept and its basic parameters stored. The
     * three options were reject the batch, drop the parameters, or store and hide
     * on read — and the middle one is the only defensible one. Rejecting would
     * cost a tenant their positions because one device over-reports; storing and
     * hiding defeats the gate's stated purpose, which is that a tenant who has not
     * bought CAN must not fill a partitioned table with data nobody may look at.
     *
     * **Reported, never silent.** An integrator sending `engineRpm` to a
     * `TELEMETRY` tenant learns it here rather than watching a column stay empty
     * and concluding the platform is broken. `droppedParameters` names the
     * DISTINCT parameters so the fix is one message rather than an investigation;
     * `parametersDropped` counts every reading, which is what says whether this is
     * one stray field or the whole payload.
     */
    parametersDropped: z.number().int(),
    droppedParameters: z.array(z.string()),
    correlationId: z.string().nullable(),
});
// ─────────────────────── Telemetry history (read) ───────────────────────
/**
 * Which hourly aggregates `VehicleTelemetryHourly` holds for each parameter.
 *
 * The single source of truth for the raw-vs-hourly difference, kept here beside
 * the parameter catalogue so the API, the docs and the tests read one list
 * rather than three. It mirrors migration 0020 exactly.
 *
 *  - `full`   — min, max, avg and n. 31 parameters; the plotted line is `avg`.
 *  - `range`  — min, max and n but NO average, because the mean is meaningless:
 *               the average of a gear number is not a gear, and a mean fault
 *               count is not a fault count.
 *  - `none`   — no rollup columns at all. Latitude and longitude: a mean
 *               position is a point in a field, and a route view is a different
 *               feature, not a line chart.
 *
 * Booleans are absent because the history endpoint rejects them by name at any
 * resolution — a state is not a quantity.
 */
export const TELEMETRY_HOURLY_AGGREGATES = {
    gearPosition: 'range',
    activeDtcCount: 'range',
    /*
     * `range`, never `full`: the MEAN of a warning level is not a warning level,
     * exactly as the mean of a gear number is not a gear. An hour that went
     * 0,0,0,3 averages to 0.75, which names no state the vehicle was ever in —
     * while the max, 3, is the fact somebody needs.
     */
    defWarningLevel: 'range',
    latitude: 'none',
    longitude: 'none',
    /*
     * ⚠️ EVERY tachograph parameter is `none`, and this is a decision rather than
     * an omission — an unlisted numeric parameter DEFAULTS to `full`, which would
     * have quietly demanded 68 rollup columns and a restatement of the rollup
     * function for readings that cannot be summarised by the hour anyway.
     *
     * Three reasons, and any one of them is sufficient:
     *
     *  1. **The counters are RESETTING accumulators.** Daily driving resets at a
     *     daily rest, weekly at the week boundary, time-to-break at a break. A
     *     max over an hour containing a reset reports the value just BEFORE the
     *     reset and hides the reset itself; a mean is noise. Neither is a fact
     *     about the driver.
     *  2. **The two enums are CATEGORIES.** `tachoTimeStateCode` explicitly has
     *     no ordering (`2` is 4.5h reached, `11` is card expiry), so a min or max
     *     names a state nobody was in. The same argument `gearPosition` makes
     *     against `full`, one step further.
     *  3. **Drivers' hours are a NOW question.** "95 minutes before this driver
     *     must stop" is answered from the LATEST raw sample. A 90-day hourly
     *     trend of a counter that resets daily answers nothing anybody asks.
     *
     * The cost is stated rather than hidden: these appear in
     * `TELEMETRY_HOURLY_UNAVAILABLE`, so the history endpoint says "cannot be
     * summarised by the hour" instead of returning an empty series that would
     * read as "this vehicle never reported it".
     *
     * The four tacho BOOLEANS are absent from this list for the reason every
     * boolean is: the history endpoint rejects them by name at any resolution.
     */
    tachoTimeToBreakMin1: 'none',
    tachoTimeToBreakMin2: 'none',
    tachoContinuousDriveMin1: 'none',
    tachoContinuousDriveMin2: 'none',
    tachoBreakMin1: 'none',
    tachoBreakMin2: 'none',
    tachoDailyDriveMin1: 'none',
    tachoDailyDriveMin2: 'none',
    tachoWeeklyDriveMin1: 'none',
    tachoWeeklyDriveMin2: 'none',
    tachoTimeToDailyRestMin1: 'none',
    tachoTimeToDailyRestMin2: 'none',
    tachoSpeedKph: 'none',
    tachoTimeStateCode1: 'none',
    tachoTimeStateCode2: 'none',
    tachoWorkState1: 'none',
    tachoWorkState2: 'none',
};
/**
 * Canonical numeric parameters that cannot be plotted at hourly resolution.
 *
 * Returned in `unavailableAtResolution` rather than as an empty series: empty
 * reads as "this vehicle never reported it", when the truth is "this cannot be
 * summarised by the hour". Two of the four are reachable from the vehicle
 * page's "Counters and load" group, so the distinction is not academic.
 */
export const TELEMETRY_HOURLY_UNAVAILABLE = Object.keys(TELEMETRY_HOURLY_AGGREGATES);
export const telemetryResolutionSchema = z.enum(['auto', 'raw', 'hourly']);
/** Windows at or below this are served raw; above it, hourly. See §3.1 of the UI plan. */
export const TELEMETRY_RAW_WINDOW_HOURS = 24;
/** Longest window a RAW request may ask for. Beyond it, raw is refused rather than truncated. */
export const TELEMETRY_RAW_MAX_HOURS = 24 * 7;
/** Longest window of any kind. 90 days of hourly buckets. */
export const TELEMETRY_MAX_HOURS = 24 * 90;
/**
 * A period during which a boolean parameter held one value.
 *
 * **A state is not a quantity**, which is why these are spans rather than
 * points: `ignitionOn` plotted on a value axis is a square wave nobody reads,
 * and averaging it produces a number that means nothing. The history endpoint
 * refuses booleans by name for that reason; this is the visualisation they
 * actually need.
 */
export const telemetryStateSpanSchema = z.object({
    value: z.boolean(),
    from: z.string(),
    /**
     * The last instant this value was OBSERVED — never "now", and never the
     * start of the next span.
     *
     * A span ends where the evidence ends. Extending it to the next reading would
     * assert the state held through a period nothing was reported in, and that is
     * the one claim a state timeline must never make.
     */
    to: z.string(),
    /**
     * True when the span ended because telemetry STOPPED rather than because the
     * state changed.
     *
     * The same rule trips apply (`trip-context-design.md` D4), and for the same
     * reason: a device silent for two hours tells us nothing about those two
     * hours. Drawn as an open end rather than a solid bar, because a solid bar
     * across a gap is a confident claim about unobserved time.
     */
    endedOnGap: z.boolean(),
    /** Samples behind this span. One sample is an observation, not a period. */
    samples: z.number().int(),
});
export const telemetryStatesQuerySchema = z
    .object({
    /** Comma-separated boolean parameter names. */
    parameters: z.string().trim().min(1),
    hours: z.coerce.number().int().positive().max(TELEMETRY_RAW_MAX_HOURS).default(24),
    /**
     * One whole UTC day, instead of a window back from now.
     *
     * ⚠️ **Added so a finding can be opened ON THE DAY IT HAPPENED.** Without it
     * a link from a two-day-old anomaly could only say "the last 48 hours",
     * which is a different window from the day being explained and would put the
     * reader in front of the wrong evidence.
     *
     * ⚠️ It OVERRIDES `hours`, and it is still bounded by what RAW holds:
     * booleans have no hourly rollup, so a day older than
     * `TELEMETRY_RAW_MAX_HOURS` cannot be shown at all. The response says which
     * window it actually read rather than quietly substituting another.
     */
    day: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
        .optional(),
})
    .strict();
export const telemetryStatesResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    from: z.string(),
    to: z.string(),
    hoursRequested: z.number().int(),
    /**
     * A requested `day` that raw telemetry can no longer reach.
     *
     * ⚠️ **Named rather than silently substituted.** Booleans have no hourly
     * rollup, so a day beyond the raw window cannot be drawn — and returning the
     * last 24 hours instead would put a reader in front of the wrong evidence
     * while the heading said the right date. Null whenever the window WAS
     * honoured.
     */
    dayOutOfRange: z.string().nullable().default(null),
    series: z.array(z.object({
        parameter: z.string(),
        spans: z.array(telemetryStateSpanSchema),
        /** Samples that carried a reading FOR THIS PARAMETER. */
        reported: z.number().int(),
        /** Observed transitions. Zero with a span means a state that never moved. */
        changes: z.number().int(),
    })),
    /**
     * Rows examined in the window, whether or not they carried any of these
     * parameters.
     *
     * The same distinction `samplesRead` draws on the vehicle track: "reported
     * nothing" and "reported, but never this parameter" produce an identical
     * empty timeline and mean opposite things — one is a silent vehicle, the
     * other a sensor that is not wired.
     */
    samplesRead: z.number().int(),
    /** Names that are not canonical parameters at all. */
    unknownParameters: z.array(z.string()),
    /**
     * Canonical parameters that are NUMERIC, echoed back rather than ignored.
     *
     * Distinguished from an unknown name because the remedy differs: one is a
     * typo, the other is "chart it, do not band it".
     */
    numericParameters: z.array(z.string()),
});
/**
 * Parameter history for one vehicle, at one of two resolutions.
 *
 * **RAW is samples, not averages**, and that is the point of it: averaging is
 * the obvious way to fit a long window on a chart and it destroys the signal
 * these parameters exist for — a coolant spike, a momentary oil-pressure drop.
 *
 * **HOURLY exists because raw cannot answer a long question.** The cap is 5,000
 * samples, which at a 30-second reporting interval is about 41 hours; a 7-day
 * raw request therefore returned 41 hours and said so. Raw is also dropped at
 * `TELEMETRY_RETENTION_DAYS`, so lifting the cap would buy a month at most.
 * `VehicleTelemetryHourly` is kept for years and is the right source for any
 * window past a day.
 *
 * A response is NEVER a mixture. One resolution for the whole payload, named in
 * the body, because a chart that silently blends measured samples with hourly
 * means is one a reader cannot interpret.
 */
export const telemetryHistoryQuerySchema = z
    .object({
    /**
     * Canonical parameter names, comma-separated. Required: fetching all 41
     * for a 24-hour window would return ~118,000 values to draw perhaps four
     * lines.
     */
    parameters: z.string().trim().min(1),
    /**
     * Window length. Default 24h. The maximum is 90 days, which only `hourly`
     * can actually serve — a raw request beyond `TELEMETRY_RAW_MAX_HOURS` is
     * refused by the service rather than quietly truncated, because a caller
     * asking for 90 days of 30-second samples has misunderstood something.
     */
    hours: z.coerce.number().int().positive().max(TELEMETRY_MAX_HOURS).default(24),
    /**
     * `auto` (the default) picks raw at or below 24 hours and hourly above it.
     * The explicit values force one, which is what a comparison view needs when
     * two vehicles must be charted at the same resolution regardless of window.
     */
    resolution: telemetryResolutionSchema.default('auto'),
})
    .strict();
export const telemetryPointSchema = z.object({
    at: z.string(),
    /**
     * The reading at `at` for raw, or the hour's MEAN for hourly.
     *
     * NULL where nothing was reported — which is normal, since not every record
     * carries every parameter, and an hour may hold no reading for one of them. A
     * chart must render it as a GAP, never as zero.
     */
    value: z.number().nullable(),
    /**
     * Hourly only. Omitted entirely on raw points rather than set to the value:
     * a raw sample is one reading, and `min === max === value` would dress a
     * measurement up as an aggregate.
     */
    min: z.number().nullable().optional(),
    max: z.number().nullable().optional(),
    /**
     * Hourly only. Raw samples behind this bucket FOR THIS PARAMETER — not the
     * hour's sample count, since a sample may report none of them. A mean over
     * three samples and one over 120 are otherwise indistinguishable, which is
     * the trap the outcome analytics already guard against.
     */
    n: z.number().int().nullable().optional(),
});
export const telemetrySeriesSchema = z.object({
    parameter: z.string(),
    /** How many points in this series actually carry a value. */
    reported: z.number().int(),
    points: z.array(telemetryPointSchema),
});
export const telemetryHistoryResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    /** The window REQUESTED, echoed. */
    from: z.string(),
    to: z.string(),
    /** Which source served this. Stated, never left for the client to infer from the shape. */
    resolution: z.enum(['raw', 'hourly']),
    /** 60 for hourly; null for raw, which has no bucket. */
    bucketMinutes: z.number().int().nullable(),
    /**
     * The window ACTUALLY covered — first and last point carrying a value, or
     * null when nothing was reported at all. A vehicle enrolled two days ago
     * cannot supply a 7-day window, and saying so beats drawing five empty days.
     */
    availableFrom: z.string().nullable(),
    availableTo: z.string().nullable(),
    /** False when the available range falls short of the requested one at either end. */
    complete: z.boolean(),
    series: z.array(telemetrySeriesSchema),
    /**
     * Raw samples examined. At hourly this is the sum of the buckets'
     * `sampleCount`, i.e. the raw samples the summary was built from — the same
     * meaning at both resolutions.
     */
    sampleCount: z.number().int(),
    /**
     * True when the window held more samples than the cap and the OLDEST were
     * dropped. Stated rather than hidden: a chart silently showing the last hour
     * of a day-long window is worse than one that says it did. Always false at
     * hourly resolution, which is the whole reason hourly exists.
     */
    truncated: z.boolean(),
    /** Names that are not canonical parameters, echoed back rather than ignored. */
    unknownParameters: z.array(z.string()),
    /**
     * Canonical numeric parameters with no aggregate at this resolution — see
     * {@link TELEMETRY_HOURLY_UNAVAILABLE}. Distinguished from `unknownParameters`
     * because the remedy differs: one is a typo, the other is "ask for a shorter
     * window".
     */
    unavailableAtResolution: z.array(z.string()),
});
/** Cap on samples examined per RAW request. Raw at 30 s is ~2,880 samples/day/vehicle. */
export const TELEMETRY_HISTORY_MAX_SAMPLES = 5_000;
/**
 * Below this many samples an hourly mean is shown as provisional.
 *
 * At a 30-second interval a full hour is ~120 samples. Six is five minutes of
 * coverage, and a mean over five minutes presented identically to a mean over
 * an hour is the kind of false precision the outcome analytics already refuse.
 */
export const TELEMETRY_LOW_SAMPLE_THRESHOLD = 6;
// ─────────────────── Vehicle CAN health summary (read) ───────────────────
/**
 * The parameters the vehicle health summary shows, in display order.
 *
 * Chosen to answer "is this vehicle healthy" in one glance, not to be
 * exhaustive — the full catalogue is one click away in the history chart. Each
 * is numeric: booleans are states and belong in a different visualisation.
 *
 * NOT here, and each for a different reason worth stating:
 *
 *  - **Idle time** — asked for, and NOT IMPLEMENTED rather than not applicable.
 *    It is derivable (engine running, speed at zero, summed over a window) but
 *    the hourly rollup stores per-parameter aggregates, not co-occurrence, so
 *    there is nothing to read it from. It needs its own rollup column or a
 *    dedicated query; inventing it from `engineRpm_avg` and
 *    `vehicleSpeedKph_avg` would be arithmetic on two independent means, which
 *    is not the same quantity at all.
 *  - **Malfunction lamp** — a boolean. `activeDtcCount` carries the same
 *    finding as a number and is shown instead.
 */
export const TELEMETRY_HEALTH_PARAMETERS = [
    'engineCoolantTempC',
    'engineOilPressureKpa',
    'engineLoadPct',
    'batteryVoltageV',
    'fuelLevelPct',
    'fuelRateLph',
    'engineRunHours',
    'odometerKm',
    'activeDtcCount',
];
/**
 * A reading older than this is reported STALE.
 *
 * Three hours, against a typical 30-second reporting interval — deliberately
 * generous, because a parked vehicle with the ignition off is not a fault and
 * a summary that shouts about every overnight stop teaches people to ignore it.
 * This is a statement about DATA FRESHNESS, not about vehicle health.
 */
export const TELEMETRY_STALE_AFTER_MINUTES = 180;
/**
 * How far back to look when deciding whether a parameter was EVER reported.
 *
 * Ninety days rather than forever: the rollup is kept for years, and scanning
 * all of it to label one card would cost more than the answer is worth. A
 * vehicle silent for three months is, for this purpose, one that has not
 * reported — and the fleet page, which looks over its own window, is where a
 * longer history is examined.
 */
export const TELEMETRY_HEALTH_HORIZON_DAYS = 90;
export const telemetryHealthQuerySchema = z
    .object({
    /**
     * The comparison window. The summary reports this period against the
     * immediately preceding period of the same length — "since yesterday" is a
     * question about change, and change needs two windows.
     */
    hours: z.coerce.number().int().positive().max(TELEMETRY_MAX_HOURS).default(24),
})
    .strict();
/**
 * Why a parameter has no current reading — the states that must not look alike.
 *
 *  - `REPORTING`      — a reading inside the freshness window.
 *  - `STALE`          — a reading exists, but older than the window. The value
 *                       is still returned: an old number with its age stated is
 *                       more use than a blank.
 *  - `NEVER_REPORTED` — nothing in the whole lookback. This vehicle does not
 *                       send it, or the sensor has never worked. NOT "zero",
 *                       and NOT the same as stale.
 */
export const telemetryHealthStatusSchema = z.enum(['REPORTING', 'STALE', 'NEVER_REPORTED']);
export const telemetryWindowStatSchema = z.object({
    avg: z.number().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    /** Raw samples behind this window FOR THIS PARAMETER. */
    n: z.number().int(),
});
export const telemetryHealthItemSchema = z.object({
    parameter: z.string(),
    status: telemetryHealthStatusSchema,
    /** The most recent reading and when it was taken. Null only when NEVER_REPORTED. */
    latest: z.number().nullable(),
    latestAt: z.string().nullable(),
    /** Age of `latestAt` in minutes, so the client need not agree on "now". */
    ageMinutes: z.number().int().nullable(),
    /**
     * The last hour this parameter was reported at all, over a long horizon.
     *
     * Distinct from `latestAt`, and deliberately so. `latest`/`latestAt` come from
     * RAW samples inside the comparison window and are an instantaneous reading;
     * this comes from the hourly rollup over months, is accurate only to the hour,
     * and carries NO value — an hour's mean is not a reading, and presenting one
     * as the other is the mixing Phase 1 exists to prevent.
     *
     * It exists so STALE and NEVER_REPORTED stay distinguishable. Inside a 48-hour
     * window a vehicle that stopped three weeks ago looks identical to one that
     * never reported, and those are different faults with different owners.
     */
    lastReportedAt: z.string().nullable(),
    /** Age of `lastReportedAt` in HOURS — it is hour-granular, so minutes would overstate it. */
    lastReportedAgeHours: z.number().int().nullable(),
    current: telemetryWindowStatSchema,
    previous: telemetryWindowStatSchema,
    /**
     * Change in the mean, current minus previous, and the same as a percentage of
     * the previous mean.
     *
     * Null — not zero — when either window is too thin to compare or the previous
     * mean is zero. A change of "0%" asserts stability; null says we cannot tell,
     * and those are different claims.
     */
    changeAbs: z.number().nullable(),
    changePct: z.number().nullable(),
    /**
     * Why `changeAbs` is null, when it is. Lets the UI explain rather than show a
     * bare dash that reads as "no change".
     */
    changeUnavailable: z
        .enum(['NO_HOURLY_AVERAGE', 'INSUFFICIENT_SAMPLES', 'PREVIOUS_ZERO'])
        .nullable(),
});
export const telemetryHealthResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    /** The current window. `previousFrom`..`from` is the comparison window. */
    from: z.string(),
    to: z.string(),
    previousFrom: z.string(),
    items: z.array(telemetryHealthItemSchema),
    /**
     * Most recent sample of ANY kind, or null if the vehicle has never reported.
     * Distinguishes "this vehicle is silent" from "this parameter is silent",
     * which need different people to fix them.
     */
    lastSampleAt: z.string().nullable(),
    /** The freshness threshold applied, so the client states the same rule the server used. */
    staleAfterMinutes: z.number().int(),
    /**
     * How far back "never reported" was checked. Stated because it changes what
     * the label MEANS: over 90 days it is a real absence, over 48 hours it would
     * only have meant "quiet this week".
     */
    horizonDays: z.number().int(),
});
// ─────────────────────── Trend and drift (read) ───────────────────────
/**
 * Parameters offered for trend analysis, in display order.
 *
 * All have a full hourly mean — a trend needs one, so `gearPosition` and
 * `activeDtcCount` cannot appear here however interesting a rising fault count
 * would be. That would need a different computation over min/max/n, not this one.
 */
export const TELEMETRY_TREND_PARAMETERS = [
    'engineCoolantTempC',
    'engineOilPressureKpa',
    'engineOilTempC',
    'batteryVoltageV',
    'fuelRateLph',
    'engineLoadPct',
    'dpfSootLoadPct',
    'exhaustGasTempC',
];
export const telemetryTrendQuerySchema = z
    .object({
    /**
     * Observation period in days. Long by default: the point of a trend is to
     * see movement too slow to notice in a day's chart, and the rollup is kept
     * for years where raw is dropped at 30 days.
     */
    days: z.coerce.number().int().min(7).max(365).default(30),
})
    .strict();
export const telemetryTrendItemSchema = z.object({
    parameter: z.string(),
    /** Null whenever `unavailable` is set. Never both, never neither. */
    direction: z.enum(['RISING', 'FALLING', 'STABLE']).nullable(),
    /**
     * Why no direction could be claimed.
     *
     *  - `TOO_FEW_DAYS`          — fewer reporting days than the minimum. Two
     *                              points define a line; the line means nothing.
     *  - `INSUFFICIENT_COVERAGE` — days exist but are too sparse across the
     *                              period to represent it.
     *  - `NO_VARIATION`          — the value never moves. A stuck sender, not a
     *                              healthy constant, and NOT the same as STABLE.
     */
    unavailable: z.enum(['TOO_FEW_DAYS', 'INSUFFICIENT_COVERAGE', 'NO_VARIATION']).nullable(),
    slopePerDay: z.number().nullable(),
    totalChange: z.number().nullable(),
    changePct: z.number().nullable(),
    /** How much of the day-to-day variation the fitted line explains, 0–1. */
    fitQuality: z.number().nullable(),
    /** Day-to-day scatter around the line, in the parameter's own units. */
    residualStdDev: z.number().nullable(),
    daysWithData: z.number().int(),
    coverage: z.number(),
    /** The thinnest day behind the fit. One sample is a reading, not a day's mean. */
    minSamplesInADay: z.number().int(),
    totalSamples: z.number().int(),
    firstDay: z.string().nullable(),
    lastDay: z.string().nullable(),
    /** The daily means the line was fitted through, so the claim is inspectable. */
    points: z.array(z.object({ day: z.string(), mean: z.number(), samples: z.number().int() })),
});
/**
 * Trend and drift over the hourly rollup.
 *
 * **These are EVIDENCE, not decisions.** No trend here creates an incident or a
 * recommendation; the endpoint reports movement and how well supported it is,
 * and a person decides what to do. That is the settled position for anything
 * predictive — a prediction is not a decision (ADR 0002).
 *
 * It also does not judge whether the movement is BAD. Rising coolant is a
 * concern and rising fuel level is a refuel, and telling them apart needs
 * per-vehicle or per-class thresholds the platform does not have. Same position
 * as the health summary, for the same reason.
 */
export const telemetryTrendResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    from: z.string(),
    to: z.string(),
    daysRequested: z.number().int(),
    items: z.array(telemetryTrendItemSchema),
    /** The thresholds applied, so the client states the same rule the server used. */
    minDaysWithData: z.number().int(),
    minCoverage: z.number(),
    stableBandPct: z.number(),
});
// ─────────────────── Fleet CAN intelligence (read) ───────────────────
/**
 * A vehicle is reported STALE when its most recent SUMMARISED hour is older
 * than this.
 *
 * Hours, not minutes, and much looser than the per-vehicle
 * `TELEMETRY_STALE_AFTER_MINUTES`. Fleet freshness is read from the hourly
 * rollup, which is up to 59 minutes behind by design, so a minute-level
 * threshold here would mark healthy vehicles stale for arithmetic reasons.
 */
export const FLEET_STALE_AFTER_HOURS = 24;
export const fleetCanQuerySchema = z
    .object({
    days: z.coerce.number().int().min(7).max(90).default(30),
    /** Cap on vehicles examined. Stated in the response when it bites. */
    limit: z.coerce.number().int().min(1).max(500).default(200),
})
    .strict();
export const fleetCanTrendSchema = z.object({
    parameter: z.string(),
    direction: z.enum(['RISING', 'FALLING']),
    changePct: z.number().nullable(),
    totalChange: z.number(),
    /**
     * The daily means behind the trend, oldest first, PADDED to the requested
     * period with `null` for days that carried no reading.
     *
     * Sent so a list can draw the shape beside the number: a steady slide and a
     * cliff both read as "−17.6%" in text, and they are not the same finding.
     * Padded rather than compacted because a gap must break the line — a vehicle
     * that reported 15 of 30 days has a different picture from one that reported
     * every day, and compacting would hide exactly that.
     *
     * Only carried for parameters that are actually MOVING, which is at most a
     * handful per vehicle, so this stays small across a fleet.
     */
    points: z.array(z.number().nullable()),
});
export const fleetCanVehicleSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    /**
     * `REPORTING` / `STALE` / `NEVER_REPORTED`, the same three the per-vehicle
     * summary uses and kept apart for the same reason: they need different people
     * to fix them.
     */
    status: telemetryHealthStatusSchema,
    /** Most recent SUMMARISED hour. Null when nothing was summarised in the window. */
    lastSummarisedAt: z.string().nullable(),
    ageHours: z.number().int().nullable(),
    /** Only the parameters actually moving. An empty array means nothing is. */
    trends: z.array(fleetCanTrendSchema),
    /** Days that carried data, and what share of the window they cover. */
    daysWithData: z.number().int(),
    coverage: z.number(),
    /**
     * True when no parameter had enough data to assess. Distinct from "assessed
     * and found stable" — one needs a longer look, the other does not.
     */
    insufficientData: z.boolean(),
});
/**
 * Fleet-wide CAN exceptions.
 *
 * **Every figure here is about a vehicle against ITS OWN history.** There is no
 * fleet median, no peer comparison and no ranking of one vehicle against
 * another, because that needs a vehicle class the platform does not model yet —
 * and comparing a van's coolant temperature to a tractor unit's produces
 * confident nonsense. When vehicle classes exist, comparison becomes additive.
 *
 * Like the per-vehicle screens it reports movement and freshness, and judges
 * neither. No thresholds exist to judge them against.
 */
export const fleetCanResponseSchema = z.object({
    from: z.string(),
    to: z.string(),
    daysRequested: z.number().int(),
    vehicles: z.array(fleetCanVehicleSchema),
    /** Counts across the vehicles examined, for the summary strip. */
    summary: z.object({
        reporting: z.number().int(),
        stale: z.number().int(),
        neverReported: z.number().int(),
        withTrends: z.number().int(),
        insufficientData: z.number().int(),
    }),
    /** Vehicles considered. Fewer than the tenant's total when `truncated`. */
    vehiclesExamined: z.number().int(),
    /**
     * True when the tenant has more vehicles than the cap. Stated rather than
     * hidden: a fleet list silently showing 200 of 900 is worse than one that
     * says so.
     */
    truncated: z.boolean(),
    staleAfterHours: z.number().int(),
    minDaysWithData: z.number().int(),
});
// ─────────────────── Cohort comparison (read) ───────────────────
export const cohortPositionSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    mean: z.number(),
    samples: z.number().int(),
    daysWithData: z.number().int(),
    /** False when the vehicle is listed but excluded from the median. */
    contributes: z.boolean(),
    deviation: z.number().nullable(),
    deviationPct: z.number().nullable(),
    /** 1 = highest. Null for non-contributors, who are not ranked. */
    rank: z.number().int().nullable(),
});
export const cohortParameterSchema = z.object({
    parameter: z.string(),
    median: z.number().nullable(),
    /**
     * Why no comparison could be made.
     *
     *  - `COHORT_TOO_SMALL` — fewer contributing vehicles than the minimum. With
     *    two there is no middle value at all.
     *  - `NO_SPREAD`        — contributors exist but every mean is identical.
     *    "0% from the median" for all of them would read as a confident finding
     *    of uniformity rather than as a parameter that does not vary.
     */
    unavailable: z.enum(['COHORT_TOO_SMALL', 'NO_SPREAD']).nullable(),
    contributors: z.number().int(),
    membersConsidered: z.number().int(),
    lowConfidence: z.boolean(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    positions: z.array(cohortPositionSchema),
});
export const cohortComparisonQuerySchema = z
    .object({
    /** The class to compare within. Comparison across classes is not offered. */
    vehicleClassId: z.string().uuid(),
    /**
     * The vehicle being compared.
     *
     * ⚠️ REQUIRED, and it is what supplies the DRIVETRAIN half of the cohort.
     * The server reads the subject's drivetrain rather than taking it from the
     * caller, so the pair can never disagree — and the question "how does this
     * compare" is not answerable without naming what is being compared.
     */
    vehicleId: z.string().uuid(),
    days: z.coerce.number().int().min(7).max(90).default(30),
})
    .strict();
/**
 * Like-for-like comparison inside one vehicle class.
 *
 * **Only within a class, never across.** A 3.5-tonne van and a tractor unit
 * have different normal coolant temperatures, fuel rates and engine loads, and
 * a median across both is a confident wrong answer. Unclassified vehicles are
 * therefore not comparable at all — they are visible everywhere else in the
 * product, and simply absent here.
 *
 * It reports POSITION and judges nothing. Running hotter than the rest of the
 * class is a fact; whether it matters needs thresholds the platform does not
 * have, exactly as on the health summary and the trend cards.
 */
export const cohortComparisonResponseSchema = z.object({
    vehicleClassId: z.string().uuid(),
    vehicleClassName: z.string(),
    from: z.string(),
    to: z.string(),
    daysRequested: z.number().int(),
    /** Vehicles assigned to this class, before any data guard. */
    vehiclesInClass: z.number().int(),
    parameters: z.array(cohortParameterSchema),
    /** The guards applied, so the client explains the same rules the server used. */
    minCohortSize: z.number().int(),
    lowConfidenceBelow: z.number().int(),
    minDaysToContribute: z.number().int(),
});
// ── where a vehicle has been ────────────────────────────────────────────────
/**
 * The longest track window served raw.
 *
 * Position has NO hourly rollup — a mean position is a point in a field the
 * vehicle may never have occupied — so a track can only ever be read from raw
 * samples, and raw is dropped at the retention boundary. There is deliberately
 * no `resolution` parameter: offering one would imply a summarised track exists.
 */
export const TRACK_MAX_HOURS = 24;
/** How many points one track may carry before it is thinned. */
export const TRACK_MAX_POINTS = 2_000;
export const trackPointSchema = z.object({
    at: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    /** Speed at that instant, when the same sample reported one. */
    speedKph: z.number().nullable(),
});
/**
 * Where a vehicle has been, oldest first.
 *
 * A track is not two series. `latitude` and `longitude` are separate canonical
 * parameters, so the history endpoint can return both — but as independent
 * lists, which a client would have to re-pair by timestamp, dropping any sample
 * that reported one and not the other. That re-pairing is where a track quietly
 * becomes wrong, so the server does it once and returns points that are already
 * positions.
 *
 * `points` therefore contains ONLY samples carrying both halves. `samplesRead`
 * says how many rows were examined, so a vehicle reporting telemetry without
 * position is distinguishable from one reporting nothing — absent is not zero,
 * at every layer.
 */
export const vehicleTrackResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    registration: z.string(),
    from: z.string(),
    to: z.string(),
    hoursRequested: z.number().int(),
    points: z.array(trackPointSchema),
    /** Rows inspected in the window, positioned or not. */
    samplesRead: z.number().int(),
    /**
     * True when the window held more positions than `TRACK_MAX_POINTS` and the
     * track was thinned by taking every Nth point.
     *
     * Named rather than silent: a thinned track cuts corners, and a reader
     * measuring a distance off it should know the line is a sketch of the route
     * rather than the route.
     */
    thinned: z.boolean(),
});
export const trackQuerySchema = z
    .object({
    hours: z.coerce.number().int().min(1).max(TRACK_MAX_HOURS).default(TRACK_MAX_HOURS),
})
    .strict();
// ───────────────────────── Tachograph (drivers' hours) ─────────────────────
/**
 * The work state the device reports, 0-3.
 *
 * ⚠️ The device's own enum is 0-7: `6 Error` and `7 Not available` are the two
 * sentinels the adapter maps to ABSENCE, so they never reach a stored value and
 * are deliberately absent here. A reader must not be offered a state meaning
 * "the tachograph could not tell us" as though the driver were in it.
 *
 * Declared in contracts so the API and the web app read ONE vocabulary. A
 * second copy is how `inferActiveTenant` diverged twice.
 */
export const TACHO_WORK_STATES = {
    0: 'Rest',
    1: 'Available',
    2: 'Work',
    3: 'Drive',
};
/**
 * AVL 189/190 — the device's own pre-warning state machine, supplied by
 * AjnaView on 14 September 2026.
 *
 * ⚠️ **A CATEGORY, NOT A SCALE, and the full list is what proves it.** Codes
 * 1–6 escalate within DAILY driving, 7–10 are WEEKLY and fortnightly, and 11–12
 * are card administration. So 11 (card expiry) is not "worse" than 6 (16h
 * reached) — they are different kinds of fact about different subjects. A
 * condition must use EQUALITY, or a derived boolean per case decided when the
 * rule is written; `>= 2` would match a card-download reminder and a driver at
 * the wheel past sixteen hours alike.
 *
 * ⚠️ **14 and 15 NEVER REACH THIS TABLE.** They are the device saying it cannot
 * answer, and the adapter maps them to ABSENCE — the same rule `ptoState: 2`
 * and work states 6/7 already follow. Stored as codes they would draw on a
 * screen as a state the driver was in, and a rule reading "state 15" would be
 * reading a broken sensor as a fact about a person.
 *
 * ⚠️ The device's own wording says "driver 1" for 11 and 12. The label here is
 * slot-neutral because the parameter exists per slot: `tachoTimeStateCode2`
 * carries the same codes about the SECOND card, and repeating "driver 1" there
 * would name the wrong person.
 */
export const TACHO_TIME_STATES = {
    0: 'Normal',
    1: '15 minutes before 4.5 h',
    2: '4.5 h reached',
    3: '15 minutes before 9 h',
    4: '9 h reached',
    5: '15 minutes before 16 h',
    6: '16 h reached',
    7: 'Weekly driving pre-warning',
    8: 'Weekly driving warning',
    9: 'Two-week driving pre-warning',
    10: 'Two-week driving warning',
    11: 'Card expiry warning',
    12: 'Card download due',
    /*
     * The device's own catch-all. Kept and labelled rather than dropped: a state
     * it declines to name is still a state it reported, and hiding it would make
     * a reader think nothing was flagged.
     */
    13: 'Other',
};
/**
 * The card slot's state as the DEVICE reports it — supplied by AjnaView on
 * 14 September 2026, having previously been collapsed to a boolean upstream.
 *
 * ⚠️ **0 and 3 are the pair this exists to separate, and they are opposite
 * findings.** *No card* in a moving vehicle is a compliance matter about a named
 * person; *privacy mode* is a driver legitimately out of scope and is not a
 * finding at all. Collapsed to `false` they were indistinguishable, so the card
 * had to warn with a caveat saying it could not tell them apart — honest, and
 * exactly what nobody wants to read.
 *
 * ⚠️ **2 (Error) NEVER REACHES THIS TABLE**, like every other device sentinel
 * here: the adapter maps it to absence. A slot whose reader is faulty is not a
 * slot in a known state.
 */
export const TACHO_CARD_STATES = {
    0: 'No card',
    1: 'Card inserted',
    3: 'Privacy mode',
};
/**
 * ⚠️ The two codes that mean "no answer", kept beside the table they are
 * excluded from so the omission reads as deliberate rather than as a gap.
 * 14 is the device reporting an error, 15 that the value is unavailable.
 */
export const TACHO_TIME_STATE_ABSENT = [14, 15];
/**
 * A tachograph's work state laid out day by day — the chart an analysis tool
 * draws, built from what the device actually sent.
 *
 * ⚠️ **It DRAWS; it does not judge.** An analysis report cites an Article and
 * names an infringement; this cannot, because the ruleset the vehicle runs
 * under is something OIP is not told (`tachograph-parameters.md` §8 Q5). An
 * invented threshold here would produce a confident compliance verdict from no
 * evidence, on the one screen where being wrong is most expensive.
 */
export const tachoActivityBandSchema = z.object({
    /** 0 Rest · 1 Available · 2 Work · 3 Drive — `TACHO_WORK_STATES`. */
    state: z.number().int().min(0).max(3),
    /** Minutes from midnight UTC, clipped to this day. */
    fromMinute: z.number(),
    toMinute: z.number(),
    /** Telemetry STOPPED rather than the state changing — drawn with an open edge. */
    endedOnGap: z.boolean(),
    /** The span crosses this day's boundary; it is clipped here, not duplicated. */
    continuesBefore: z.boolean(),
    continuesAfter: z.boolean(),
});
export const tachoActivityDaySchema = z.object({
    /** `YYYY-MM-DD`, UTC. */
    date: z.string(),
    bands: z.array(tachoActivityBandSchema),
    /**
     * Minutes per state, keyed by the state code — OBSERVED, never a day's
     * totals. A device heard from for six hours yields six hours.
     */
    minutes: z.record(z.string(), z.number()),
    observedMinutes: z.number(),
    /**
     * ⚠️ ZERO is the value that matters: the tachograph reported nothing for this
     * day. It draws the same empty row as a day of rest and means the opposite.
     */
    reported: z.number().int(),
});
export const tachoActivitySlotSchema = z.object({
    /** 1 or 2. Slot 2 is the second crew member and is usually empty. */
    slot: z.number().int().min(1).max(2),
    days: z.array(tachoActivityDaySchema),
    /** Samples carrying a work state for this slot across the whole window. */
    reported: z.number().int(),
});
export const tachoActivityResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    vehicleName: z.string(),
    /** Oldest first, one entry per UTC day in the window — including silent ones. */
    days: z.array(z.string()),
    slots: z.array(tachoActivitySlotSchema),
    /**
     * ⚠️ **The window is bounded by RAW RETENTION and says so.** Every tacho
     * parameter declares `none` for hourly aggregates deliberately — the counters
     * are resetting accumulators and the two enums are categories, so an hourly
     * min or max would name a state nobody was in. That leaves raw samples as the
     * only source, and raw is dropped at `TELEMETRY_RETENTION_DAYS`. A two-month
     * control range is not something this platform can answer, and a chart that
     * quietly showed thirty days under a request for sixty would read as the
     * fleet having been idle for a month.
     */
    retentionDays: z.number().int(),
    requestedDays: z.number().int(),
    /** True where `requestedDays` was cut down to what retention can serve. */
    truncated: z.boolean(),
    /**
     * ⚠️ Every instant is UTC, stated rather than assumed. A drivers'-hours day
     * is a real legal boundary and a fleet on summer time reads an hour early;
     * the alternative is a per-tenant timezone this platform does not hold, and
     * silently picking the server's would be worse than saying which was used.
     */
    timeZone: z.literal('UTC'),
});
/**
 * The vehicles a tachograph activity chart can actually be drawn for.
 *
 * ⚠️ **A SEPARATE list from the drivers'-hours table, because the windows
 * differ.** That table is a fixed 24 hours — *who has to stop soon* — and using
 * it to populate a 28-day chart's picker would hide the chart on exactly the
 * fleet whose devices went quiet, which is when looking back matters most.
 */
export const tachoVehicleSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    /** The last work state this vehicle recorded inside the window. */
    lastAt: z.string().datetime(),
});
export const tachoVehiclesResponseSchema = z.object({
    items: z.array(tachoVehicleSchema),
    /** Days covered, after the retention cap. */
    days: z.number().int(),
    retentionDays: z.number().int(),
    /**
     * Active vehicles in the fleet — the DENOMINATOR. Most vehicles carry no
     * tachograph, so "3 of 107" is the difference between a short list and a
     * fleet that has stopped reporting.
     */
    activeVehicles: z.number().int(),
});
export const tachoActivityQuerySchema = z
    .object({
    /** UTC days ending today. Capped at retention, and the cap is reported. */
    days: z.coerce.number().int().min(1).max(60).default(7),
})
    .strict();
/**
 * One driver slot, as ONE sample reported it.
 *
 * ⚠️ **Never assembled field-by-field across samples**, and this is the rule
 * that keeps the card honest. These counters are a coherent snapshot — the
 * sample's continuous drive and its time to break sum to the 4h30 limit — so
 * taking the newest value of each field independently would produce a set that
 * looks authoritative and describes no instant that ever happened. A reader
 * checking the arithmetic would find it wrong, on the one screen where the
 * arithmetic is the point.
 *
 * `reportedAt` is therefore per SLOT: a single-crewed vehicle reports slot 1
 * continuously and slot 2 never, and one timestamp for both would date the
 * empty slot to the moment the full one last spoke.
 */
export const tachoSlotSchema = z.object({
    slot: z.union([z.literal(1), z.literal(2)]),
    /**
     * Who this slot's counters belong to, where the card could be resolved.
     *
     * ⚠️ **NULL is the ordinary state and must not read as an error.** It means
     * the device sent no card identifier, or sent one this tenant has not
     * recorded against a driver — neither is a fault, and both are far more
     * common than a resolved name until cards are enrolled. The card says
     * "Driver 1" / "Driver 2" then, exactly as it always did.
     *
     * Taken from the SAME sample as the slot's counters, never merged: a name
     * from one instant beside figures from another would attribute somebody
     * else's hours to a person who was not in the seat.
     */
    driverId: z.string().uuid().nullable(),
    driverName: z.string().nullable(),
    /**
     * When the sample these values came from was recorded.
     *
     * ⚠️ NULLABLE, because a slot that never reported has no instant of its own.
     * Most vehicles are single-crewed, so an empty slot 2 is the common case —
     * substituting the epoch would print 1970 on the card, and substituting the
     * OTHER slot's timestamp would date an empty slot to the moment the full one
     * last spoke. Both are inventions; a null is the fact.
     */
    reportedAt: z.string().datetime().nullable(),
    /**
     * ⚠️ A boolean because AjnaView collapses the device's 0-3 upstream, so
     * `false` merges *no card* — a compliance finding — with *privacy mode*,
     * which is not one. Documented rather than hidden: no care at our end
     * recovers information destroyed before it reaches us.
     */
    cardPresent: z.boolean().nullable(),
    workState: z.number().int().nullable(),
    workStateLabel: z.string().nullable(),
    /** Minutes. The single most operational fact in the payload. */
    timeToBreakMin: z.number().nullable(),
    continuousDriveMin: z.number().nullable(),
    breakMin: z.number().nullable(),
    dailyDriveMin: z.number().nullable(),
    weeklyDriveMin: z.number().nullable(),
    timeToDailyRestMin: z.number().nullable(),
    /**
     * The device's own pre-warning state machine (AVL 189/190).
     *
     * ⚠️ ALWAYS NULL today — AjnaView does not send it (`tachograph-parameters.md`
     * section 8, Q1). Present in the shape so a client is written against the
     * field rather than around its absence, and it is a CATEGORY not a scale:
     * `2` is *4.5h reached*, `11` is *card expiry*. No ordering is invented, so
     * no label is offered for it either.
     */
    /**
     * The precise 0–3 slot state, where the device sent one.
     *
     * ⚠️ **`cardPresent` is NOT replaced and must not be.** Every row stored
     * before 14 September 2026 has only the boolean — the value AjnaView
     * collapsed upstream — and `false` there is genuinely ambiguous between *no
     * card* and *privacy mode*. Converting it would invent a compliance finding
     * on 1,514 development rows alone; dropping it would lose the fact that the
     * device reported anything. So the two sit side by side and the caveat is
     * shown exactly where the precise value is MISSING, rather than everywhere or
     * nowhere.
     */
    cardState: z.number().int().nullable(),
    cardStateLabel: z.string().nullable(),
    timeStateCode: z.number().int().nullable(),
    /**
     * What the device's pre-warning code MEANS — `TACHO_TIME_STATES`.
     *
     * ⚠️ Beside the code rather than replacing it. The code is what a rule
     * matches on and what an integrator reads; the label is for the reader, and
     * an unrecognised code keeps a null label rather than an invented one — 14
     * and 15 are mapped to absence by the adapter and should never arrive, so a
     * code here without a label is a finding about the pipeline, not a state.
     */
    timeStateLabel: z.string().nullable(),
});
/**
 * Why there is nothing to show — never a bare empty response.
 *
 * The distinction `samplesRead` exists to draw, made explicit rather than left
 * for each client to re-derive. A vehicle that sent 600 samples and no tacho
 * parameter HAS NO TACHOGRAPH — normal, nothing to chase; one that sent nothing
 * at all has a DEVICE THAT STOPPED — a finding. Both draw an empty card, and
 * merging them sends somebody to look for a fault in a lorry that never had the
 * part.
 */
export const tachoAbsenceSchema = z.enum([
    /** The tenant does not hold `TACHOGRAPH`. The route is gated, so this is for completeness. */
    'NOT_ENTITLED',
    /** No telemetry at all in the window — a question about the DEVICE. */
    'NO_TELEMETRY',
    /** Telemetry arrived and carried no tacho parameter — a question about the VEHICLE'S FIT. */
    'NO_TACHOGRAPH',
]);
export const vehicleTachoResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    windowHours: z.number().int(),
    /**
     * Every sample in the window, tacho or not. The DENOMINATOR — it is what lets
     * a caller tell the two absences apart before it says either.
     */
    samplesRead: z.number().int(),
    /** Of those, how many carried at least one tachograph parameter. */
    tachoSamplesRead: z.number().int(),
    /** The instant of the most recent tacho reading, or null if there is none. */
    asOf: z.string().datetime().nullable(),
    /** Always two entries, slot 1 then slot 2. A slot that never reported is all-null. */
    slots: z.array(tachoSlotSchema),
    /**
     * The tachograph's OWN speed and over-speed judgement.
     *
     * Kept beside `vehicleSpeedKph` rather than merged into it: the tachograph is
     * the legally recorded source and the tracker is not, so the two can
     * legitimately disagree and a merge would silently pick a winner. Which
     * should be preferred is note section 7 and is not decided.
     */
    speedKph: z.number().nullable(),
    overSpeed: z.boolean().nullable(),
    driverRecognised: z.boolean().nullable(),
    absence: tachoAbsenceSchema.nullable(),
});
/**
 * One vehicle's drivers'-hours standing, for the fleet view.
 *
 * Slot 1 only. The fleet question is *who must stop soon*, and a crewed vehicle
 * has a second driver who is resting — including them would list a person with
 * hours in hand beside people running out of them, on a screen sorted by
 * urgency. The vehicle page shows both slots, which is where that belongs.
 */
export const fleetTachoRowSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    reportedAt: z.string().datetime(),
    timeToBreakMin: z.number().nullable(),
    continuousDriveMin: z.number().nullable(),
    dailyDriveMin: z.number().nullable(),
    weeklyDriveMin: z.number().nullable(),
    workState: z.number().int().nullable(),
    workStateLabel: z.string().nullable(),
    cardPresent: z.boolean().nullable(),
    overSpeed: z.boolean().nullable(),
    /**
     * Minutes of break taken so far.
     *
     * ⚠️ **The missing half of "On a break".** A resting driver reported zero
     * minutes remaining and the page said only that they had stopped — so it
     * could not answer the question anybody asks next, which is when they are
     * available again. Null while driving, because a break not being taken is not
     * a break of length zero.
     */
    breakMin: z.number().nullable(),
    /**
     * Minutes before the daily rest the DEVICE says is due.
     *
     * ⚠️ **The second limit, and it binds independently of the first.** A driver
     * fresh from a break has hours before the next one and may be twenty minutes
     * from the end of their permitted day — so a page ordered on the break alone
     * can put the most constrained driver on the fleet near the bottom. That is
     * the fault this page already fixed once for resting drivers.
     */
    timeToDailyRestMin: z.number().nullable(),
    /**
     * Whether the device recognised the card's driver.
     *
     * Stated, never judged: an unrecognised card is a fact about the reading, not
     * an accusation, and the provider does not say why.
     */
    driverRecognised: z.boolean().nullable(),
    /**
     * Slot 2 carried something in the SAME sample.
     *
     * ⚠️ The page shows slot 1 only, which understates a crewed vehicle — this
     * marker is what tells a reader the row is half the story rather than leaving
     * them to discover it on the vehicle page.
     */
    hasSecondDriver: z.boolean(),
});
export const fleetTachoResponseSchema = z.object({
    items: z.array(fleetTachoRowSchema),
    /**
     * ⚠️ The DENOMINATOR, and the reason this is not just a list.
     *
     * Six rows on a fleet of eighty-eight looks like the fleet unless something
     * says otherwise — the argument `/fleet/positions` makes with
     * `activeVehicles`. Most vehicles have no tachograph, which is NORMAL and not
     * a gap to chase, so the page states the ratio rather than implying the rest
     * are missing.
     */
    activeVehicles: z.number().int(),
    windowHours: z.number().int(),
});
//# sourceMappingURL=telemetry.js.map